// packages/shared-domain/lib/tenant/vendorMembers.ts
// PRODUCT_DEV-811: 「そのテナントにとって当社（Umeee）スタッフか」を判定する**唯一の場所**。
//
// 当社スタッフはサポートのために顧客テナントへ**外から**参加する。実体は `UserTenants` に
// `PK=USER#{sub}` / `SK=TENANT#{顧客tenantId}` の行が1本増えるだけで、**ベンダであるという
// 印はスキーマ上に無い**。そのまま出すと、顧客の日報・週報・月報のメンバー一覧や
// 担当者ピッカーに当社の人間が並ぶ。
//
// ⚠ **`tenantId === UMEEE_TENANT_ID` の比較をこのファイルの外に書かないこと。**
// 「当社テナントを見ているときは誰も当社スタッフ扱いしない」（そこは本来の所属先で、
// 全員外すと自社での動作確認ができなくなる）という例外が要るのは判定のときだけで、
// 呼び出し側に配ると必ず食い違う。外へは `createVendorPredicate` が返す述語だけを渡す。
//
// ⚠ **判定は「所属」と「メールドメイン」の OR。** どちらか片方では取りこぼす:
//   - 所属だけ → `corp@umeee.co.jp` のように当社テナントに membership を持たない
//     当社アカウントが顧客テナントに居る（2026-09-02 時点の prod で 2 テナントに実在）
//   - メールだけ → 当社テナントには gmail 等で登録されたアカウントもある
//
// ⚠ **ログインの有無や role では絞らない。** 当社スタッフの行は role=admin で入ることが
// 多いが、role は「そのテナントでの権限」であってベンダ性を表さない。sysadmin フラグも
// 顧客テナントの管理者に付けられないという保証がコード上に無いので、識別子にしない。
//
// ⚠ **在籍判定（status）はこのファイルの責務ではない。** 呼び出し側の在籍述語と
// 組み合わせて使う（unizo なら `apps/unizo/api/lib/tenantMembers.ts` の
// `isActiveMembership`）。理由を混ぜると「なぜ消えたか」が読めなくなる。
//
// ⚠ **専用 AWS アカウント配備（`infra/tenants/*.ts` の `operatorTenantId: null`、例: とちぎん）
// では当社テナントそのものが存在しない。** 所属の集合が空になり、判定は実質メールドメイン
// だけに縮退する。黙って弱くならないよう `onVendorTenantEmpty` で呼び出し側へ知らせる。

/** 当社テナントの ID。正本は `infra/tenants/umeee.ts` の `operatorTenantId`。 */
export const UMEEE_TENANT_ID = "umeee-3jz2";

/** 当社アカウントのメールドメイン。 */
const UMEEE_MAIL_DOMAIN = "@umeee.co.jp";

const isVendorEmail = (email: string | undefined | null): boolean =>
  typeof email === "string" && email.toLowerCase().endsWith(UMEEE_MAIL_DOMAIN);

/** 判定に必要な最小の形。`email` は無くてもよい（その場合は所属だけで判定される）。 */
export interface VendorCandidate {
  userId: string;
  email?: string;
}

/** 当社スタッフか否かを返す述語。**当社テナント向けに作ると常に false を返す。** */
export type VendorPredicate = (user: VendorCandidate) => boolean;

/**
 * 当社テナントの会員を引ければよいので、リポジトリ全体ではなくこの1メソッドだけ要求する。
 *
 * ⚠ **構造的な型にしてあるのは意図的。** `UserTenantDDBRepository` を import すると
 * DDB / aws-sdk / middy の型グラフがこのモジュールにぶら下がり、`UMEEE_TENANT_ID` を
 * 再エクスポートしている web 側の型検査にまで引き込まれる。
 */
export interface VendorMemberRepo {
  getUsersByTenantId(tenantId: string): Promise<Array<{ userId: string }>>;
}

// コンテナ内に 60 秒だけ持つ（`auth-service/cacheUsers.ts` と同じ考え方・同じ TTL）。
// 当社メンバーの増減が反映されるまで最大 1 分遅れるが、その間に起きるのは
// 「増えた人が 1 分だけ顧客側に見える」だけで、権限には影響しない。
//
// `auth-service/lruCache` は使わない。`lib/*` から `auth-service/*` へ依存する向きは
// このリポジトリに前例が無く（依存は auth-service → lib の一方向）、値が1つしか無い
// キャッシュのために逆流を作る理由が無い。
const VENDOR_MEMBER_IDS_TTL_MS = 60_000;
let vendorMemberIdsCache: { ids: ReadonlySet<string>; exp: number } | null =
  null;

/** テスト用にキャッシュを捨てる（本番コードからは呼ばない）。 */
export const clearVendorMemberIdsCache = (): void => {
  vendorMemberIdsCache = null;
};

const loadVendorMemberIds = async (
  userRepo: VendorMemberRepo,
): Promise<ReadonlySet<string>> => {
  if (vendorMemberIdsCache && vendorMemberIdsCache.exp > Date.now()) {
    return vendorMemberIdsCache.ids;
  }
  const members = await userRepo.getUsersByTenantId(UMEEE_TENANT_ID);
  const ids: ReadonlySet<string> = new Set(members.map((m) => m.userId));
  vendorMemberIdsCache = { ids, exp: Date.now() + VENDOR_MEMBER_IDS_TTL_MS };
  return ids;
};

export interface VendorPredicateOptions {
  /**
   * 顧客テナントを見ているのに当社テナントの会員が1人も引けなかったときに呼ばれる。
   * 専用アカウント配備では正常だが、共有アカウントで起きたら判定が壊れている合図。
   * **PII は渡さない**（呼び出し側でログに出すことを想定している）。
   */
  onVendorTenantEmpty?: () => void;
}

/**
 * そのテナント用の「当社スタッフか」判定器を作る。
 *
 * - `tenantId` が当社テナントなら **DDB を引かず**、常に false を返す述語を返す
 * - それ以外は当社テナントの会員集合を1回引き（60秒キャッシュ）、所属 OR メールで判定する
 *
 * ⚠ **当社テナントの Query が失敗したら例外がそのまま伝播する（fail-closed）。**
 * 除外できないまま一覧を返すと顧客の画面に当社スタッフが出てしまうので、
 * 「出さないより落ちる」を選んでいる。failure-soft にしたい経路は呼び出し側で捕まえること。
 */
export const createVendorPredicate = async (
  userRepo: VendorMemberRepo,
  tenantId: string,
  options?: VendorPredicateOptions,
): Promise<VendorPredicate> => {
  if (tenantId === UMEEE_TENANT_ID) return () => false;
  const ids = await loadVendorMemberIds(userRepo);
  if (ids.size === 0) options?.onVendorTenantEmpty?.();
  return (user) => ids.has(user.userId) || isVendorEmail(user.email);
};

/**
 * PRODUCT_DEV-1235: その人に**システム管理者（sysadmin）を付けてよいか**＝当社スタッフか。
 *
 * `createVendorPredicate` は「顧客の画面から当社スタッフを外す」ための判定で、当社テナントを見ているときは
 * 常に false を返す（自社では誰も外さない）。付与の判定ではその逆で、**当社テナントの所属行なら当社の人**として通す。
 * 顧客テナントでは、当社テナントの会員か当社のメールドメインの人だけを通す。
 *
 * ⚠ 当社テナントの Query が失敗したら例外がそのまま伝播する（付けない側に倒す）。
 */
export const isVendorForGrant = async (
  userRepo: VendorMemberRepo,
  tenantId: string,
  user: VendorCandidate,
): Promise<boolean> => {
  if (tenantId === UMEEE_TENANT_ID) return true;
  const isVendor = await createVendorPredicate(userRepo, tenantId);
  return isVendor(user);
};
